/**
 * Invoice Styles - Predefined style definitions for invoice PDFs
 *
 * These styles are designed to match the existing invoice appearance
 * while providing consistent formatting across all invoice components.
 */

import type { StyleDictionary } from 'pdfmake/interfaces';
import { PDF_COLORS } from '../types';

/**
 * Default styles for invoice PDFs
 */
export const invoiceStyles: StyleDictionary = {
    // Letterhead styles
    letterheadName: {
        fontSize: 16,
        bold: true,
        margin: [0, 0, 0, 2],
    },
    letterheadTagline: {
        fontSize: 10,
        italics: true,
        color: PDF_COLORS.textMuted,
        margin: [0, 0, 0, 2],
    },
    letterheadLine: {
        fontSize: 10,
        margin: [0, 0, 0, 1],
    },
    letterheadAddress: {
        fontSize: 9,
        color: PDF_COLORS.textMuted,
        margin: [0, 2, 0, 0],
    },

    // Client info styles
    clientInfoLabel: {
        fontSize: 9,
        color: PDF_COLORS.textMuted,
        margin: [0, 0, 0, 1],
    },
    clientInfoValue: {
        fontSize: 10,
        margin: [0, 0, 0, 4],
    },
    clientName: {
        fontSize: 12,
        bold: true,
        margin: [0, 0, 0, 4],
    },
    invoiceNumber: {
        fontSize: 10,
    },

    // Section headers
    sectionHeader: {
        fontSize: 14,
        bold: true,
        margin: [0, 25, 0, 15],
    },
    subSectionHeader: {
        fontSize: 11,
        bold: true,
        margin: [0, 10, 0, 5],
    },

    // Table styles (no background colors - matches original clean look)
    tableHeader: {
        fontSize: 9,
        bold: true,
        margin: [4, 4, 4, 4],
    },
    tableCell: {
        fontSize: 9,
        margin: [4, 2, 4, 2],
    },
    tableFooter: {
        fontSize: 9,
        bold: true,
        margin: [4, 4, 4, 4],
    },

    // Account summary styles
    accountSummaryTotal: {
        fontSize: 10,
        bold: true,
        fillColor: PDF_COLORS.totalGreen,
    },
    accountSummarySubtotal: {
        fontSize: 9,
        margin: [15, 0, 0, 0],
    },
    accountSummaryAmountDue: {
        fontSize: 11,
        bold: true,
        fillColor: PDF_COLORS.amountDueYellow,
    },

    // Trust accounting styles
    trustAccountHeader: {
        fontSize: 11,
        bold: true,
    },
    trustAccountBalance: {
        fontSize: 11,
        bold: true,
    },
    trustTransaction: {
        fontSize: 9,
    },
    trustPositive: {
        fontSize: 9,
        color: PDF_COLORS.positiveGreen,
    },
    trustNegative: {
        fontSize: 9,
        color: PDF_COLORS.negativeRed,
    },

    // QR code styles
    qrCodeLabel: {
        fontSize: 10,
        color: '#1976d2',
        decoration: 'underline',
    },

    // Invoice detail (detailed mode) styles
    taskDescription: {
        fontSize: 9,
    },
    taskDate: {
        fontSize: 9,
        color: PDF_COLORS.textMuted,
    },
    taskUser: {
        fontSize: 9,
    },
    taskAmount: {
        fontSize: 9,
        alignment: 'right',
    },

    // Invoice summary (narrative mode) styles
    narrativeHeader: {
        fontSize: 12,
        bold: true,
        margin: [0, 15, 0, 10],
    },
    narrativeDate: {
        fontSize: 10,
        bold: true,
        margin: [0, 8, 0, 3],
    },
    narrativeText: {
        fontSize: 10,
        margin: [0, 0, 0, 5],
    },

    // Footer styles
    footer: {
        fontSize: 8,
        color: PDF_COLORS.textMuted,
        alignment: 'center',
    },

    // Misc
    muted: {
        color: PDF_COLORS.textMuted,
    },
    bold: {
        bold: true,
    },
    right: {
        alignment: 'right',
    },
    center: {
        alignment: 'center',
    },
};

/**
 * Court invoice styles - more minimal/formal
 */
export const courtInvoiceStyles: StyleDictionary = {
    ...invoiceStyles,
    // Override specific styles for court format
    letterheadName: {
        ...invoiceStyles.letterheadName,
        fontSize: 14,
    },
    sectionHeader: {
        fontSize: 12,
        bold: true,
        margin: [0, 18, 0, 12],
    },
};

/**
 * Get styles based on invoice type, with optional font size scaling.
 *
 * When baseFontSize differs from the default (10pt), all fontSize values
 * in the style dictionary are scaled proportionally.
 *
 * @param courtInvoice - Use court invoice styles
 * @param baseFontSize - Configured base font size in points (default: 10)
 */
export function getInvoiceStyles(courtInvoice = false, baseFontSize = 10): StyleDictionary {
    const base = courtInvoice ? courtInvoiceStyles : invoiceStyles;
    if (baseFontSize === 10) return base;

    const ratio = baseFontSize / 10;
    const scaled: StyleDictionary = {};
    for (const [key, style] of Object.entries(base)) {
        if (style.fontSize) {
            scaled[key] = { ...style, fontSize: Math.round(style.fontSize * ratio) };
        } else {
            scaled[key] = style;
        }
    }
    return scaled;
}

export default invoiceStyles;
